import type {
  LoginRequest,
  OtpDeliveryMethod,
  OtpGenerateRequest,
  OtpVerifyRequest,
  RegisterRequest,
} from '@/types/auth.types';

export const REGISTER_REQUEST = 'auth/REGISTER_REQUEST' as const;
export const REGISTER_SUCCESS = 'auth/REGISTER_SUCCESS' as const;
export const REGISTER_FAILURE = 'auth/REGISTER_FAILURE' as const;

export const LOGIN_REQUEST = 'auth/LOGIN_REQUEST' as const;
export const LOGIN_SUCCESS = 'auth/LOGIN_SUCCESS' as const;
export const LOGIN_FAILURE = 'auth/LOGIN_FAILURE' as const;

export const GENERATE_OTP_REQUEST = 'auth/GENERATE_OTP_REQUEST' as const;
export const GENERATE_OTP_SUCCESS = 'auth/GENERATE_OTP_SUCCESS' as const;
export const GENERATE_OTP_FAILURE = 'auth/GENERATE_OTP_FAILURE' as const;

export const VERIFY_OTP_REQUEST = 'auth/VERIFY_OTP_REQUEST' as const;
export const VERIFY_OTP_SUCCESS = 'auth/VERIFY_OTP_SUCCESS' as const;
export const VERIFY_OTP_FAILURE = 'auth/VERIFY_OTP_FAILURE' as const;

export const CLEAR_AUTH_ERROR = 'auth/CLEAR_AUTH_ERROR' as const;
export const RESET_AUTH_STATE = 'auth/RESET_AUTH_STATE' as const;

export interface RegisterRequestAction {
  type: typeof REGISTER_REQUEST;
  payload: RegisterRequest;
}

export interface RegisterSuccessAction {
  type: typeof REGISTER_SUCCESS;
  payload: string;
}

export interface RegisterFailureAction {
  type: typeof REGISTER_FAILURE;
  payload: string;
}

export interface LoginRequestAction {
  type: typeof LOGIN_REQUEST;
  payload: LoginRequest;
}

export interface LoginSuccessAction {
  type: typeof LOGIN_SUCCESS;
  payload: {
    message: string;
    mobileNumber: string;
    email: string | null;
    transactionId: string | null;
    deliveryMethod: OtpDeliveryMethod | null;
  };
}

export interface LoginFailureAction {
  type: typeof LOGIN_FAILURE;
  payload: string;
}

export interface GenerateOtpRequestAction {
  type: typeof GENERATE_OTP_REQUEST;
  payload: OtpGenerateRequest;
}

export interface GenerateOtpSuccessAction {
  type: typeof GENERATE_OTP_SUCCESS;
  payload: {
    message: string;
    maskedDestination: string;
    deliveryMethod: OtpDeliveryMethod;
  };
}

export interface GenerateOtpFailureAction {
  type: typeof GENERATE_OTP_FAILURE;
  payload: string;
}

export interface VerifyOtpRequestAction {
  type: typeof VERIFY_OTP_REQUEST;
  payload: OtpVerifyRequest;
}

export interface VerifyOtpSuccessAction {
  type: typeof VERIFY_OTP_SUCCESS;
  payload: {
    message: string;
    accessToken: string | null;
    refreshToken: string | null;
    customerId: string | null;
  };
}

export interface VerifyOtpFailureAction {
  type: typeof VERIFY_OTP_FAILURE;
  payload: string;
}

export interface ClearAuthErrorAction {
  type: typeof CLEAR_AUTH_ERROR;
}

export interface ResetAuthStateAction {
  type: typeof RESET_AUTH_STATE;
}

export type AuthActionTypes =
  | RegisterRequestAction
  | RegisterSuccessAction
  | RegisterFailureAction
  | LoginRequestAction
  | LoginSuccessAction
  | LoginFailureAction
  | GenerateOtpRequestAction
  | GenerateOtpSuccessAction
  | GenerateOtpFailureAction
  | VerifyOtpRequestAction
  | VerifyOtpSuccessAction
  | VerifyOtpFailureAction
  | ClearAuthErrorAction
  | ResetAuthStateAction;

export const registerRequest = (payload: RegisterRequest): RegisterRequestAction => ({
  type: REGISTER_REQUEST,
  payload,
});

export const registerSuccess = (payload: string): RegisterSuccessAction => ({
  type: REGISTER_SUCCESS,
  payload,
});

export const registerFailure = (payload: string): RegisterFailureAction => ({
  type: REGISTER_FAILURE,
  payload,
});

export const loginRequest = (payload: LoginRequest): LoginRequestAction => ({
  type: LOGIN_REQUEST,
  payload,
});

export const loginSuccess = (payload: LoginSuccessAction['payload']): LoginSuccessAction => ({
  type: LOGIN_SUCCESS,
  payload,
});

export const loginFailure = (payload: string): LoginFailureAction => ({
  type: LOGIN_FAILURE,
  payload,
});

export const generateOtpRequest = (payload: OtpGenerateRequest): GenerateOtpRequestAction => ({
  type: GENERATE_OTP_REQUEST,
  payload,
});

export const generateOtpSuccess = (
  payload: GenerateOtpSuccessAction['payload']
): GenerateOtpSuccessAction => ({
  type: GENERATE_OTP_SUCCESS,
  payload,
});

export const generateOtpFailure = (payload: string): GenerateOtpFailureAction => ({
  type: GENERATE_OTP_FAILURE,
  payload,
});

export const verifyOtpRequest = (payload: OtpVerifyRequest): VerifyOtpRequestAction => ({
  type: VERIFY_OTP_REQUEST,
  payload,
});

export const verifyOtpSuccess = (payload: VerifyOtpSuccessAction['payload']): VerifyOtpSuccessAction => ({
  type: VERIFY_OTP_SUCCESS,
  payload,
});

export const verifyOtpFailure = (payload: string): VerifyOtpFailureAction => ({
  type: VERIFY_OTP_FAILURE,
  payload,
});

export const clearAuthError = (): ClearAuthErrorAction => ({
  type: CLEAR_AUTH_ERROR,
});

export const resetAuthState = (): ResetAuthStateAction => ({
  type: RESET_AUTH_STATE,
});
